import { Component, OnInit } from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import { BancoService } from 'src/app/service/banco/banco.service';
import { Banco } from 'src/app/entity/Banco';
import { criarRestituicaoForm } from './restituicao-form.builder';
import { RestituicaoRequest, mapRestituicaoFormToRequest, AnexoRequest } from './restituicao-form.dto';
import {
  DOCUMENTOS_ANEXOS_PADRAO,
  DocumentoAnexoOption,
  MOTIVOS_RESTITUICAO_PADRAO,
  MotivoRestituicaoOption
} from './restituicao-form.options';
import {ERRO_DADOS_OBRIGATORIOS} from "../../../../../../tools/Mensagens";

@Component({
  selector: 'app-restituicao-form',
  templateUrl: './restituicao-form.component.html',
  styleUrls: ['./restituicao-form.component.css']
})
export class RestituicaoFormComponent implements OnInit {
  form: FormGroup;

  motivos: MotivoRestituicaoOption[] = [];
  bancos: Banco[] = [];
  documentosAnexos: DocumentoAnexoOption[] = DOCUMENTOS_ANEXOS_PADRAO;

  anexosSelecionados: Record<string, string> = {};
  private anexosMap: Record<string, AnexoRequest> = {};

  constructor(
    private fb: FormBuilder,
    private bancoService: BancoService
  ) {
    // monta o form usando a função de fábrica separada
    this.form = criarRestituicaoForm(this.fb);
  }

  onArquivoSelecionado(event: Event, id: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;

    if (!file) {
      delete this.anexosSelecionados[id];
      delete this.anexosMap[id];
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result && result.indexOf('base64,') >= 0
        ? result.substring(result.indexOf('base64,') + 7)
        : '';

      this.anexosSelecionados[id] = file.name;
      this.anexosMap[id] = {
        nomeArquivo: file.name,
        conteudo: base64,
        type: file.type
      };
    };

    reader.readAsDataURL(file);
  }

  ngOnInit(): void {
    // carrega bancos da API
    this.bancoService.carregarDados().subscribe(
      (bancos: Banco[]) => this.bancos = bancos,
      err => console.log(err)
    );

    // Simula carregamento assíncrono das opções de motivo.
    setTimeout(() => {
      this.motivos = MOTIVOS_RESTITUICAO_PADRAO;
    }, 0);

    const tipoRestituicaoControl = this.form.get('tipoRestituicao');
    const tipoDocumentoContaControl = this.form.get('tipoDocumentoConta');
    const cpfCnpjTitularContaControl = this.form.get('cpfCnpjTitularConta');

    if (tipoRestituicaoControl) {
      this.atualizarValidacaoValorRestituir(tipoRestituicaoControl.value);

      tipoRestituicaoControl.valueChanges.subscribe((tipo: string) => {
        this.atualizarValidacaoValorRestituir(tipo);
      });
    }

    if (tipoDocumentoContaControl && cpfCnpjTitularContaControl) {
      tipoDocumentoContaControl.valueChanges.subscribe((tipoDocumento: 'CPF' | 'CNPJ') => {
        const valorAtual = cpfCnpjTitularContaControl.value;
        const valorNormalizado = this.normalizarDocumentoTitular(valorAtual, tipoDocumento);

        cpfCnpjTitularContaControl.setValue(valorNormalizado, { emitEvent: false });
        cpfCnpjTitularContaControl.updateValueAndValidity({ emitEvent: false });
      });
    }
  }

  /**
   * Método público para que componentes pais possam obter o payload de restituição
   * aplicando as mesmas validações usadas no submit local.
   */
  getRestituicaoPayloadIfValid(): RestituicaoRequest | null {
    return this.buildPayloadIfValid();
  }

  /**
   * Monta o payload de restituição aplicando validações.
   * Retorna null caso o formulário esteja inválido.
   */
  private buildPayloadIfValid(): RestituicaoRequest | null {
    this.form.markAllAsTouched();
    console.log(this.form, this.anexosMap);

    if (!this.form.valid) {
      const cpfRequerenteControl = this.form.get('cpfRequerente');
      const cpfCnpjTitularContaControl = this.form.get('cpfCnpjTitularConta');
      const tipoDocumentoContaControl = this.form.get('tipoDocumentoConta');

      const cpfRequerenteInvalido =
        cpfRequerenteControl &&
        cpfRequerenteControl.errors &&
        cpfRequerenteControl.errors.cpfInvalido;

      const cpfTitularInvalido =
        tipoDocumentoContaControl &&
        tipoDocumentoContaControl.value === 'CPF' &&
        cpfCnpjTitularContaControl &&
        cpfCnpjTitularContaControl.errors &&
        cpfCnpjTitularContaControl.errors.cpfInvalido;

      const cnpjTitularInvalido =
        tipoDocumentoContaControl &&
        tipoDocumentoContaControl.value === 'CNPJ' &&
        cpfCnpjTitularContaControl &&
        cpfCnpjTitularContaControl.errors &&
        cpfCnpjTitularContaControl.errors.cnpjInvalido;

      if (cpfRequerenteInvalido || cpfTitularInvalido || cnpjTitularInvalido) {
        alert('O CPF/CNPJ informado está incorreto. Verifique os dados e tente novamente.');
      }

      return null;
    }

    if (!this.documentosValidos()) {
      return null;
    }

    const raw = this.form.value;


    const anexos: AnexoRequest[] = Object.keys(this.anexosMap).map(key => this.anexosMap[key]);
    console.log(anexos);

    return mapRestituicaoFormToRequest(raw, anexos);
  }

  private atualizarValidacaoValorRestituir(tipoRestituicao: string): void {
    const control = this.form.get('valorRestituir');
    if (!control) {
      return;
    }

    if (tipoRestituicao === 'P') {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
      control.setValue(null);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private documentosValidos(): boolean {
    const titularContaControl = this.form.get('titularConta');
    const titularConta = titularContaControl ? titularContaControl.value : null;

    if (titularConta === 'P') {
      const temProcuracao = !!this.anexosMap['procuracao'];

      if (!temProcuracao) {
        alert('Para requerente do tipo Procurador, o envio da Procuração é obrigatório.');
        return false;
      }
    }

    const anexosObrigatorios = [
      'docIdentidadeLegitimado',
      'cnpjServentia',
      'cdtCpfResponsavel',
      'guiaBoletoEComprovante'
    ];

    const temAnexoAusente = anexosObrigatorios
      .some(key => !this.anexosMap[key]);

    if (temAnexoAusente) {
      alert('Existem anexos obrigatórios não enviados.');
      return false;
    }
    return true;
  }

  get mostrarDvAgencia(): boolean {
    const bancoControl = this.form.get('banco');
    if (!bancoControl) {
      return false;
    }

    const banco = bancoControl.value as Banco | null;
    if (!banco) {
      return false;
    }

    const id = banco.id;
    return id === 1 || id === 104 || id === 237;
  }

  onBankFieldKeyDown(event: KeyboardEvent, maxDigits: number): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    // Permite teclas de navegação/edição normalmente
    if (event.key.length !== 1) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const currentDigits = this.onlyDigits(input.value);
    const isFilled = currentDigits.length >= maxDigits;

    // Campo completo: bloqueia qualquer nova digitação para evitar sobrescrita/inserção.
    if (isFilled) {
      event.preventDefault();
    }
  }

  private onlyDigits(value: string): string {
    return String(value || '').replace(/\D/g, '');
  }

  private normalizarDocumentoTitular(value: any, tipoDocumento: 'CPF' | 'CNPJ'): string {
    const documento = String(value || '');

    if (tipoDocumento === 'CPF') {
      return documento.replace(/\D/g, '');
    }

    return documento.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
  }
}
